import React, { useEffect, useState } from 'react'
import { NavLink, useHistory } from 'react-router-dom'
import { superAxiosRequest } from 'axiosApi'
import cogoToast from 'cogo-toast'
import useTableFixedHeader from 'hooks/useTableFixedHeader'
import ContentHeader from '../ContentHeader'
import THead from './THead'
import Loader from '../Loader/Loader'
import st from './Table.module.scss'
import ExportExcelButton from 'components/Modules/Reports/ExportExcelButton/ExportExcelButton'
import { ExcelRowsParams, setCellParams } from 'components/utils/ExcelExport'
import SearchBooks from 'components/Modules/Ebook/Books/components/SearchBooks/SearchBooks'
import FilterTextValues from 'components/UI/AsideFilter/FilterTextValues'
import AsideFilter from 'components/UI/AsideFilter/AsideFilter'
import useLocalStorageFilter from 'helpers/useLocalStorageFilter'
import AsideFilterStore from 'components/UI/AsideFilter/asideFilterStore'
import useLocalization from 'hooks/useLocalization'
import { observer } from 'mobx-react'

function WorkedHours() {
  const cfg = {
    cols: [
      {
        name: '№ группы',
        classes: [st.bgw],
      },
      {
        name: 'Студент',
        classes: [st.fz12],
      },
      {
        name: 'Кол-во академических часов',
        classes: [st.fz12],
      },
      {
        name: 'Оплачено',
        classes: [st.fz12],
      },
      {
        name: 'Списание',
        classes: [st.fz12],
      },
      {
        name: 'Остаток',
        classes: [st.fz12],
      },
    ],
  }
  const {
    refWrapper,
    refSticky,
    refThead,
    data,
    setData,
    isLoading,
    setIsLoading,
  } = useTableFixedHeader()
  const [groupCatalog, setGroupCatalog] = useState({})
  const [filter, updateFilter] = useLocalStorageFilter()
  const history = useHistory()
  const {
    isOpenFilter,
    closeFilter,
    setTextValues,
    changeTextValue,
    filterTextValues,
  } = AsideFilterStore
  const { translate } = useLocalization({
    section: 'ebooks',
    subsection: 'ebooks_books',
    dict: {
      edit: 'Edit',
      copy: 'Copy',
      delete: 'Delete',
      save: 'Save',
      filter: 'Filter',
      company: 'Company',
      reset_all: 'Reset all',
      apply: 'Apply',
      search: 'Search',
      start_date: 'Start date',
      end_date: 'End date',
      paid_time: 'Paid time',
    },
  })

  const filterCfg = [
    {
      type: 'select',
      label: 'Локация',
      placeholder: 'Локация',
      values: groupCatalog?.locations,
      sort_key: 'name',
      nameState: 'location',
    },
    {
      type: 'select',
      label: 'Уровень группы',
      placeholder: 'Уровень группы',
      values: groupCatalog?.group_levels,
      sort_key: 'name',
      nameState: 'group_level',
    },
    {
      type: 'select',
      label: 'Тип группы',
      placeholder: 'Тип группы',
      values: groupCatalog?.group_types,
      sort_key: 'name',
      nameState: 'group_type',
    },
    {
      type: 'date_day_picker',
      nameState: ['date_from', 'date_until'],
      label: 'Период',
      isRequired: true,
    },
  ]

  const convertToAcademicHours = (hours) => {
    if (typeof hours !== 'number') return 0
    const minutes = hours * 60
    return (minutes / 45).toFixed(2)
  }

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        const response = await superAxiosRequest({
          method: 'get',
          link: 'groups/reports/worked_hours_full',
          params: filter,
        })

        setGroupCatalog((prev) => ({
          locations: prev.locations || response.data.locations,
          group_levels: response.data.group_levels || prev.group_levels,
          group_types: response.data.group_types || prev.group_types,
        }))

        setData(response.data)
      } catch (error) {
        setData({})
        cogoToast.warn('Что-то пошло не так :(', { heading: 'Ошибка сервера' })
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [filter])

  useEffect(() => {
    return AsideFilterStore.unmountFilter
  }, [])

  useEffect(() => {
    const unlisten = history.listen((location) => {
      if (location.pathname !== '/reports/worked-hours') {
        updateFilter({})
      }
    })
    return () => {
      unlisten()
    }
  }, [history, updateFilter])

  const downloadExcel = (data) => {
    import('xlsx-js-style').then((XLSX) => {
      let headRow = [
        {
          v: '№ группы',
        },
        {
          v: 'Студент',
        },
        {
          v: 'Кол-во академических часов',
        },
        {
          v: 'Оплачено',
        },
        {
          v: 'Списание',
        },
        {
          v: 'Остаток',
        },
      ]
      let tableData = []
      let merges = []
      let wscols = [
        { wch: 30 },
        { wch: 30 },
        { wch: 30 },
        { wch: 30 },
        { wch: 30 },
        { wch: 30 },
      ]

      headRow = setCellParams(headRow, ExcelRowsParams.headerRowParam)

      let currentRow = 1
      data.groups.forEach((group) => {
        if (group.students.length > 1) {
          merges.push({
            s: { r: currentRow, c: 0 },
            e: { r: currentRow + group.students.length - 1, c: 0 },
          })
        }
        group.students.forEach((student) => {
          currentRow = currentRow + 1
          tableData.push([
            {
              v: `${group.groupData.name} ${group.groupData.number}`,
              ...ExcelRowsParams.tableRowParam.firstCell,
            },
            {
              v: `${student.name}`,
              ...ExcelRowsParams.tableRowParam.cell,
            },
            {
              v: `${convertToAcademicHours(student.hours_academ)}`,
              ...ExcelRowsParams.tableRowParam.cell,
            },
            {
              v: `${student.paid}`,
              ...ExcelRowsParams.tableRowParam.cell,
            },
            {
              v: `${student.withdraw}`,
              ...ExcelRowsParams.tableRowParam.cell,
            },
            {
              v: `${student.balance}`,
              ...ExcelRowsParams.tableRowParam.cell,
            },
          ])
        })
        currentRow = currentRow + 1
        tableData.push([
          {
            v: `итого по группе`,
            ...ExcelRowsParams.footerRowParams.firstCell,
          },
          {
            v: ``,
            ...ExcelRowsParams.footerRowParams.cell,
          },
          {
            v: `${convertToAcademicHours(group.total.hours_academ)}`,
            ...ExcelRowsParams.footerRowParams.cell,
          },
          {
            v: `${group.total.paid}`,
            ...ExcelRowsParams.footerRowParams.cell,
          },
          {
            v: `${group.total.withdraw}`,
            ...ExcelRowsParams.footerRowParams.cell,
          },
          {
            v: `${group.total.balance}`,
            ...ExcelRowsParams.footerRowParams.cell,
          },
        ])
        currentRow = currentRow + 1
        tableData.push([
          {
            v: `итого среднее`,
            ...ExcelRowsParams.footerRowParams.firstCell,
          },
          {
            v: `${group.students.length}`,
            ...ExcelRowsParams.footerRowParams.cell,
          },
          {
            v: `${convertToAcademicHours(group.total.average.hours_academ)}`,
            ...ExcelRowsParams.footerRowParams.cell,
          },
          {
            v: `${group.total.average.paid}`,
            ...ExcelRowsParams.footerRowParams.cell,
          },
          {
            v: `${group.total.average.withdraw}`,
            ...ExcelRowsParams.footerRowParams.cell,
          },
          {
            v: `${group.total.average.balance}`,
            ...ExcelRowsParams.footerRowParams.cell,
          },
        ])
      })

      let footerRow = [
        {
          v: `ИТОГО`,
          ...ExcelRowsParams.footerRowParams.firstCell,
        },
        {
          v: ``,
          ...ExcelRowsParams.footerRowParams.cell,
        },
        {
          v: `${convertToAcademicHours(data.total.hours_academ)}`,
          ...ExcelRowsParams.footerRowParams.cell,
        },
        {
          v: `${data.total.paid}`,
          ...ExcelRowsParams.footerRowParams.cell,
        },
        {
          v: `${data.total.withdraw}`,
          ...ExcelRowsParams.footerRowParams.cell,
        },
        {
          v: `${data.total.balance}`,
          ...ExcelRowsParams.footerRowParams.cell,
        },
      ]

      let wb = XLSX.utils.book_new()
      const ws = XLSX.utils.aoa_to_sheet([headRow, ...tableData, footerRow])
      ws['!cols'] = wscols
      ws['!merges'] = merges
      XLSX.utils.book_append_sheet(wb, ws, 'List 1')

      XLSX.writeFile(wb, 'Отчеты Оплаченное время.xlsx')
    })
  }
  const formatNumberWithSpace = (number) => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  }
  const isFilterEmpty = !filterTextValues.length

  return (
    <>
      <ContentHeader title={translate?.paid_time} />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          margin: '10px',
        }}
      >
        <SearchBooks
          onSearch={(text) => {
            updateFilter({ ...filter, search: text })
            changeTextValue('search', {
              key: 'search',
              label: 'Search',
              value: text,
            })
          }}
          onClear={() => {
            const { search, ...obj } = filter
            updateFilter(obj)
            setTextValues(
              filterTextValues.filter((field) => field.key !== 'search'),
            )
          }}
          translate={translate}
          placeholder={`Search...`}
          value={filter?.search || ''}
          isFilterEmpty={isFilterEmpty}
        />
        {!!filterTextValues.length && (
          <FilterTextValues
            onDelete={(key) => {
              const { [key]: _, ...obj } = filter
              updateFilter(obj)
            }}
          />
        )}

        {isOpenFilter && (
          <AsideFilter
            translate={translate}
            getFilterParams={updateFilter}
            config={filterCfg}
            filterValue={filter}
            onDropFilter={() => {
              updateFilter({})
              closeFilter()
            }}
          />
        )}
      </div>
      {isLoading ? (
        <Loader />
      ) : Array.isArray(data?.groups) && data.groups.length > 0 ? (
        <>
          <div style={{ padding: '10px' }}>
            <ExportExcelButton data={data} downloadExcel={downloadExcel} />
          </div>
          <div className={st.tableWrapper_h} ref={refWrapper}>
            <div ref={refSticky} className={st.sticky}>
              <table className={st.table}>
                <THead cols={cfg.cols} />
              </table>
            </div>
            <table className={st.table}>
              <THead cols={cfg.cols} ref={refThead} />
              <tbody className={`${st.tbody} ${st.tbodyStriped}`}>
                {/* Итого по schedules && workshops */}
                {data.total && (
                  <tr className={st.bdt}>
                    <td colSpan={2} className={`${st.tdTotal} ${st.fw600}`}>
                      ИТОГО
                    </td>
                    <td
                      className={`${st.tdTotal} ${st.tdMiddle} ${st.textAlignCenter}`}
                    >
                      <div className={`${st.groupDescription} ${st.medium}`}>
                        {formatNumberWithSpace(
                          convertToAcademicHours(data.total.hours_academ),
                        )}
                      </div>
                    </td>
                    <td
                      className={`${st.tdTotal} ${st.tdMiddle} ${st.textAlignCenter}`}
                    >
                      <div className={`${st.groupDescription} ${st.medium}`}>
                        {formatNumberWithSpace(data.total.paid)}
                      </div>
                    </td>
                    <td
                      className={`${st.tdTotal} ${st.tdMiddle} ${st.textAlignCenter}`}
                    >
                      <div className={`${st.groupDescription} ${st.medium}`}>
                        {formatNumberWithSpace(data.total.withdraw)}
                      </div>
                    </td>
                    <td
                      className={`${st.tdTotal} ${st.tdMiddle} ${st.textAlignCenter}`}
                    >
                      <div className={`${st.groupDescription} ${st.medium}`}>
                        {formatNumberWithSpace(data.total.balance)}
                      </div>
                    </td>
                  </tr>
                )}
                {Array.isArray(data.groups) &&
                  data.groups.map((group, groupId) => (
                    <React.Fragment key={`g${groupId}`}>
                      <tr className={st.bdt}>
                        <td colSpan={2} className={`${st.tdTotal} ${st.fw600}`}>
                          итого по группе
                        </td>
                        <td
                          className={`${st.tdTotal} ${st.tdMiddle} ${st.textAlignCenter}`}
                        >
                          <div
                            className={`${st.groupDescription} ${st.medium}`}
                          >
                            {formatNumberWithSpace(
                              convertToAcademicHours(group.total.hours_academ),
                            )}
                          </div>
                        </td>
                        <td
                          className={`${st.tdTotal} ${st.tdMiddle} ${st.textAlignCenter}`}
                        >
                          <div
                            className={`${st.groupDescription} ${st.medium}`}
                          >
                            {formatNumberWithSpace(group.total.paid)}
                          </div>
                        </td>
                        <td
                          className={`${st.tdTotal} ${st.tdMiddle} ${st.textAlignCenter}`}
                        >
                          <div
                            className={`${st.groupDescription} ${st.medium}`}
                          >
                            {formatNumberWithSpace(group.total.withdraw)}
                          </div>
                        </td>
                        <td
                          className={`${st.tdTotal} ${st.tdMiddle} ${st.textAlignCenter}`}
                        >
                          <div
                            className={`${st.groupDescription} ${st.medium}`}
                          >
                            {formatNumberWithSpace(group.total.balance)}
                          </div>
                        </td>
                      </tr>

                      <tr className={st.bdt}>
                        <td colSpan={1} className={`${st.tdTotal} ${st.fw600}`}>
                          итого среднее
                        </td>
                        <td
                          className={`${st.tdTotal} ${st.tdMiddle} ${st.textAlignCenter}`}
                        >
                          <div
                            className={`${st.groupDescription} ${st.medium}`}
                          >
                            {Number.isInteger(group?.students?.length)
                              ? group?.students?.length
                              : group?.students?.length.toFixed(2)}
                          </div>
                        </td>
                        <td
                          className={`${st.tdTotal} ${st.tdMiddle} ${st.textAlignCenter}`}
                        >
                          <div
                            className={`${st.groupDescription} ${st.medium}`}
                          >
                            {group?.total?.average?.hours_academ != null &&
                            !isNaN(group?.total?.average?.hours_academ)
                              ? convertToAcademicHours(
                                  group?.total?.average?.hours_academ,
                                )
                              : '—'}
                          </div>
                        </td>
                        <td
                          className={`${st.tdTotal} ${st.tdMiddle} ${st.textAlignCenter}`}
                        >
                          <div
                            className={`${st.groupDescription} ${st.medium}`}
                          >
                            {formatNumberWithSpace(group?.total?.average?.paid)}
                          </div>
                        </td>
                        <td
                          className={`${st.tdTotal} ${st.tdMiddle} ${st.textAlignCenter}`}
                        >
                          <div
                            className={`${st.groupDescription} ${st.medium}`}
                          >
                            {formatNumberWithSpace(
                              group?.total?.average?.withdraw,
                            )}
                          </div>
                        </td>
                        <td
                          className={`${st.tdTotal} ${st.tdMiddle} ${st.textAlignCenter}`}
                        >
                          <div
                            className={`${st.groupDescription} ${st.medium}`}
                          >
                            {formatNumberWithSpace(
                              group?.total?.average?.balance,
                            )}
                          </div>
                        </td>
                      </tr>

                      {group.students.map((student, studentId) => (
                        <tr key={studentId} className={st.bdt}>
                          {studentId === 0 && (
                            <td
                              rowSpan={group.students.length}
                              className={st.fw600}
                              style={{ background: 'white' }}
                            >
                              <div
                                className={`${st.groupDescription} ${st.medium}`}
                              >
                                <NavLink
                                  to={`/groups/group/${group.groupData.id}`}
                                  target='_blank'
                                >
                                  {`${group.groupData.name} ${group.groupData.number}`}
                                </NavLink>
                              </div>
                            </td>
                          )}
                          <td
                            className={`${st.tdMiddle} ${st.textAlignCenter}`}
                          >
                            <div
                              className={`${st.groupDescription} ${st.bottom}`}
                            >
                              <NavLink
                                to={`/account_clients/${student.user_id}`}
                                target='_blank'
                              >
                                {student.name}
                              </NavLink>
                            </div>
                          </td>
                          <td
                            className={`${st.tdMiddle} ${st.textAlignCenter}`}
                          >
                            <div
                              className={`${st.groupDescription} ${st.medium}`}
                            >
                              {formatNumberWithSpace(
                                convertToAcademicHours(student.hours_academ),
                              )}
                            </div>
                          </td>
                          <td
                            className={`${st.tdMiddle} ${st.textAlignCenter}`}
                          >
                            <div
                              className={`${st.groupDescription} ${st.medium}`}
                            >
                              {formatNumberWithSpace(student.paid)}
                            </div>
                          </td>
                          <td
                            className={`${st.tdMiddle} ${st.textAlignCenter}`}
                          >
                            <div
                              className={`${st.groupDescription} ${st.medium}`}
                            >
                              {formatNumberWithSpace(student.withdraw)}
                            </div>
                          </td>
                          <td
                            className={`${st.tdMiddle} ${st.textAlignCenter}`}
                          >
                            <div
                              className={`${st.groupDescription} ${st.medium}`}
                            >
                              {formatNumberWithSpace(student.balance)}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <h1 className={st.noData}>Нет данных для отображения!</h1>
      )}
    </>
  )
}
export default observer(WorkedHours)
